import { useEffect, useRef } from "react";
import { useSettings } from "../context/SettingsContext";

const THEME_COLORS = {
  holi: { primary: "#e91e63", hover: "#c2185b", accent: "#9c27b0" },
  diwali: { primary: "#ea580c", hover: "#c2410c", accent: "#d4af37" },
  eid: { primary: "#059669", hover: "#047857", accent: "#fbbf24" },
  christmas: { primary: "#dc2626", hover: "#b91c1c", accent: "#16a34a" },
  republic_day: { primary: "#ea580c", hover: "#c2410c", accent: "#16a34a" },
  none: { primary: "#C79A2E", hover: "#A88025", accent: "#0a2140" },
};

export const FestiveEffects = () => {
  const { settings } = useSettings();
  const canvasRef = useRef(null);
  const theme = settings?.festival_theme || "none";
  const colors = THEME_COLORS[theme] || THEME_COLORS.none;

  useEffect(() => {
    // Dynamic CSS overrides injecting festival theme colors
    const styleEl = document.createElement("style");
    styleEl.id = "festive-theme-overrides";
    styleEl.innerHTML = `
      :root {
        --theme-primary: ${colors.primary};
        --theme-hover: ${colors.hover};
        --theme-accent: ${colors.accent};
      }
      .bg-\\[\\#D4AF37\\] { background-color: var(--theme-primary) !important; }
      .bg-\\[\\#B8860B\\] { background-color: var(--theme-primary) !important; }
      .hover\\:bg-\\[\\#FFDF73\\]:hover { background-color: var(--theme-hover) !important; }
      .hover\\:bg-\\[\\#9a7009\\]:hover { background-color: var(--theme-hover) !important; }
      
      .text-\\[\\#B8860B\\] { color: var(--theme-primary) !important; }
      .text-\\[\\#C79A2E\\] { color: var(--theme-primary) !important; }
      .hover\\:text-\\[\\#C79A2E\\]:hover { color: var(--theme-hover) !important; }
      .hover\\:text-\\[\\#B8860B\\]:hover { color: var(--theme-hover) !important; }
      
      .border-\\[\\#e6d9a8\\] { border-color: var(--theme-primary) !important; }
    `;
    document.head.appendChild(styleEl);

    return () => {
      styleEl.remove();
    };
  }, [colors]);

  useEffect(() => {
    if (theme === "none") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId;
    let particles = [];
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);

    // Particle Classes
    class HoliSplatter {
      constructor(x, y, dx, dy, color, size) {
        this.x = x;
        this.y = y;
        this.dx = dx;
        this.dy = dy;
        this.color = color;
        this.size = size;
        this.alpha = 1;
        this.decay = Math.random() * 0.02 + 0.015;
      }
      update() {
        this.x += this.dx;
        this.y += this.dy;
        this.dy += 0.08; // gravity
        this.alpha -= this.decay;
      }
      draw(c) {
        c.save();
        c.globalAlpha = this.alpha;
        c.shadowBlur = 4;
        c.shadowColor = "rgba(0, 0, 0, 0.1)";
        c.fillStyle = this.color;
        c.beginPath();
        c.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        c.fill();
        c.restore();
      }
    }

    class SnowParticle {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * -height;
        this.size = Math.random() * 3 + 1.5;
        this.speed = Math.random() * 1.2 + 0.4;
        this.wind = Math.random() * 0.4 - 0.2;
        this.isSymbol = Math.random() < 0.35;
        const symbols = ["❄", "❅", "❆"];
        this.symbol = symbols[Math.floor(Math.random() * symbols.length)];
        this.rotation = Math.random() * 360;
        this.rotSpeed = Math.random() * 2 - 1;
        this.fontSize = Math.random() * 8 + 10;
      }
      update() {
        this.y += this.speed;
        this.x += this.wind;
        this.rotation += this.rotSpeed;
        if (this.y > height) {
          this.y = -15;
          this.x = Math.random() * width;
        }
      }
      draw(c) {
        c.save();
        c.shadowBlur = 3;
        c.shadowColor = "rgba(0, 0, 0, 0.15)";
        if (this.isSymbol) {
          c.translate(this.x, this.y);
          c.rotate((this.rotation * Math.PI) / 180);
          c.fillStyle = "#a5f3fc"; // soft icy cyan/blue
          c.font = `${this.fontSize}px sans-serif`;
          c.textAlign = "center";
          c.textBaseline = "middle";
          c.fillText(this.symbol, 0, 0);
        } else {
          c.fillStyle = "#e0f2fe"; // soft icy blue
          c.beginPath();
          c.arc(this.x, this.y, this.size, 0, Math.PI * 2);
          c.fill();
        }
        c.restore();
      }
    }

    class DiwaliDiyaSpark {
      constructor() {
        this.x = Math.random() * width;
        this.y = height + 10;
        this.size = Math.random() * 4 + 1;
        this.speedY = -(Math.random() * 2 + 1);
        this.speedX = Math.random() * 0.8 - 0.4;
        this.alpha = 1;
        this.decay = Math.random() * 0.008 + 0.004;
        this.color = `hsl(${Math.random() * 30 + 35}, 100%, ${Math.random() * 20 + 50}%)`; // Golden glow
      }
      update() {
        this.y += this.speedY;
        this.x += this.speedX;
        this.alpha -= this.decay;
        if (this.alpha <= 0) {
          this.alpha = 1;
          this.y = height + 10;
          this.x = Math.random() * width;
        }
      }
      draw(c) {
        c.save();
        c.globalAlpha = this.alpha;
        c.shadowBlur = 10;
        c.shadowColor = this.color;
        c.fillStyle = this.color;
        c.beginPath();
        c.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        c.fill();
        c.restore();
      }
    }

    class DiwaliRocket {
      constructor() {
        this.x = Math.random() * width;
        this.y = height + 10;
        this.targetY = Math.random() * (height * 0.4) + height * 0.1;
        this.speedY = -(Math.random() * 4 + 5);
        this.size = 2.5;
        this.exploded = false;
      }
      update() {
        this.y += this.speedY;
        if (this.y <= this.targetY) {
          this.exploded = true;
        }
      }
      draw(c) {
        c.save();
        c.shadowBlur = 8;
        c.shadowColor = "#ffb000";
        c.fillStyle = "#ffffff";
        c.beginPath();
        c.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        c.fill();
        c.restore();
      }
    }

    class TriColorConfetti {
      constructor() {
        this.x = Math.random() * width;
        this.y = -20;
        this.size = Math.random() * 6 + 4;
        this.speedY = Math.random() * 2 + 1.5;
        this.speedX = Math.random() * 1 - 0.5;
        this.rotation = Math.random() * 360;
        this.rotSpeed = Math.random() * 4 - 2;
        const colors_tc = ["#FF9933", "#a8a29e", "#138808"]; // Saffron, Darker white/stone, Green
        this.color = colors_tc[Math.floor(Math.random() * colors_tc.length)];
      }
      update() {
        this.y += this.speedY;
        this.x += this.speedX;
        this.rotation += this.rotSpeed;
        if (this.y > height) {
          this.y = -20;
          this.x = Math.random() * width;
        }
      }
      draw(c) {
        c.save();
        c.translate(this.x, this.y);
        c.rotate((this.rotation * Math.PI) / 180);
        c.shadowBlur = 2;
        c.shadowColor = "rgba(0,0,0,0.1)";
        c.fillStyle = this.color;
        c.fillRect(-this.size / 2, -this.size / 2, this.size, this.size / 2);
        c.restore();
      }
    }

    class EidStarParticle {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * -height;
        this.speedY = Math.random() * 0.8 + 0.3;
        this.speedX = Math.random() * 0.2 - 0.1;
        this.isMoon = Math.random() < 0.15;
        this.size = Math.random() * 6 + 8;
        this.rotation = Math.random() * 360;
        this.rotSpeed = Math.random() * 0.8 - 0.4;
        this.alpha = Math.random() * 0.5 + 0.5;
        this.alphaDir = Math.random() < 0.5 ? 0.01 : -0.01;
      }
      update() {
        this.y += this.speedY;
        this.x += this.speedX;
        this.rotation += this.rotSpeed;

        this.alpha += this.alphaDir;
        if (this.alpha >= 1 || this.alpha <= 0.4) {
          this.alphaDir = -this.alphaDir;
        }

        if (this.y > height) {
          this.y = -20;
          this.x = Math.random() * width;
        }
      }
      draw(c) {
        c.save();
        c.globalAlpha = this.alpha;
        c.translate(this.x, this.y);
        c.rotate((this.rotation * Math.PI) / 180);
        c.shadowBlur = 4;
        c.shadowColor = "rgba(0,0,0,0.15)";
        c.font = `${this.size}px sans-serif`;
        c.fillStyle = this.isMoon ? "#fbbf24" : "#fbbf24";
        c.textAlign = "center";
        c.textBaseline = "middle";
        c.fillText(this.isMoon ? "🌙" : "✨", 0, 0);
        c.restore();
      }
    }

    // Initialize Particles
    if (theme === "christmas") {
      particles = Array.from({ length: 80 }, () => new SnowParticle());
    } else if (theme === "diwali") {
      particles = Array.from({ length: 40 }, () => new DiwaliDiyaSpark());
    } else if (theme === "republic_day") {
      particles = Array.from({ length: 60 }, () => new TriColorConfetti());
    } else if (theme === "eid") {
      particles = Array.from({ length: 50 }, () => new EidStarParticle());
    }

    // Global Click Listener for Holi paint splatter and Diwali micro-firework bursts
    const handleGlobalClick = (e) => {
      const x = e.clientX;
      const y = e.clientY;

      if (theme === "holi") {
        const holiColors = ["#e91e63", "#9c27b0", "#ffeb3b", "#00bcd4", "#4caf50", "#ff9800"];
        for (let i = 0; i < 20; i++) {
          const angle = Math.random() * Math.PI * 2;
          const speed = Math.random() * 4 + 1;
          particles.push(
            new HoliSplatter(
              x,
              y,
              Math.cos(angle) * speed,
              Math.sin(angle) * speed - 2,
              holiColors[Math.floor(Math.random() * holiColors.length)],
              Math.random() * 5 + 3
            )
          );
        }
      } else if (theme === "diwali") {
        const diwaliColors = ["#FFD700", "#FF8C00", "#FF4500", "#FF0000", "#FF69B4"];
        for (let i = 0; i < 25; i++) {
          const angle = Math.random() * Math.PI * 2;
          const speed = Math.random() * 3 + 1;
          const spark = new DiwaliDiyaSpark();
          spark.x = x;
          spark.y = y;
          spark.speedX = Math.cos(angle) * speed;
          spark.speedY = Math.sin(angle) * speed;
          spark.color = diwaliColors[Math.floor(Math.random() * diwaliColors.length)];
          particles.push(spark);
        }
      }
    };
    window.addEventListener("click", handleGlobalClick);

    // Animation Loop
    const loop = () => {
      ctx.clearRect(0, 0, width, height);

      // Randomly spawn Diwali rockets
      if (theme === "diwali" && Math.random() < 0.015 && particles.filter(p => p instanceof DiwaliRocket).length < 2) {
        particles.push(new DiwaliRocket());
      }

      // In Holi, drift static background color blobs occasionally
      if (theme === "holi" && Math.random() < 0.08) {
        const holiColors = ["#e91e631a", "#9c27b01a", "#ffeb3b1a", "#00bcd41a", "#4caf501a"];
        particles.push(
          new HoliSplatter(
            Math.random() * width,
            Math.random() * height,
            Math.random() * 0.4 - 0.2,
            Math.random() * 0.4 - 0.2,
            holiColors[Math.floor(Math.random() * holiColors.length)],
            Math.random() * 25 + 15
          )
        );
      }

      // Update and draw
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.update();
        p.draw(ctx);

        // Rocket explodes and spawns starburst sparks
        if (p instanceof DiwaliRocket && p.exploded) {
          particles.splice(i, 1);
          const diwaliColors = ["#FFD700", "#FF8C00", "#FF4500", "#FF0000", "#FF69B4"];
          for (let k = 0; k < 25; k++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = Math.random() * 2.5 + 1;
            const spark = new DiwaliDiyaSpark();
            spark.x = p.x;
            spark.y = p.y;
            spark.speedX = Math.cos(angle) * speed;
            spark.speedY = Math.sin(angle) * speed;
            spark.color = diwaliColors[Math.floor(Math.random() * diwaliColors.length)];
            particles.push(spark);
          }
        }

        // Holi/burst splatters decay and are removed
        else if ((theme === "holi" || p.decay) && p.alpha <= 0) {
          particles.splice(i, 1);
        }
      }

      animationFrameId = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("click", handleGlobalClick);
      cancelAnimationFrame(animationFrameId);
    };
  }, [theme]);

  if (theme === "none") return null;

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-50 w-full h-full"
    />
  );
};
